import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { Search, Calendar, DollarSign, Clock, CheckCircle, XCircle, Eye, X, Filter, BarChart3, RefreshCw, Building } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  formatCurrency,
  formatDate,
  getBookingAmount,
  normalizeBookingStatus,
} from '../utils/formatters';
import Pagination from '../components/Pagination';

const Bookings = () => {
  const { colors } = useTheme();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, searchTerm, filterStatus]);

  const fetchBookings = async () => {
    setLoading(true);
    setError('');

    try {
      const bookingsQuery = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(bookingsQuery);
      
      const bookingsData = await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          
          let guestName = 'Unknown Guest';
          let guestAvatar = null;
          let guestEmail = '';
          if (data.guestId) {
            try {
              const guestDoc = await getDoc(doc(db, 'users', data.guestId));
              if (guestDoc.exists()) {
                guestName = guestDoc.data().displayName || guestDoc.data().email || 'Unknown Guest';
                guestAvatar = guestDoc.data().profileImageUrl || null;
                guestEmail = guestDoc.data().email || '';
              }
            } catch (e) {}
          }

          let propertyTitle = 'Unknown Property';
          let propertyImage = null;
          if (data.propertyId) {
            try {
              const propertyDoc = await getDoc(doc(db, 'properties', data.propertyId));
              if (propertyDoc.exists()) {
                propertyTitle = propertyDoc.data().title || 'Untitled Property';
                propertyImage = propertyDoc.data().imageUrls?.[0] || null;
              }
            } catch (e) {}
          }

          return {
            id: docSnapshot.id,
            ...data,
            status: normalizeBookingStatus(data.status || data.bookingStatus),
            amount: getBookingAmount(data),
            checkIn: data.startDate || data.checkInDate || data.checkIn || null,
            checkOut: data.endDate || data.checkOutDate || data.checkOut || null,
            guestName,
            guestEmail,
            guestAvatar,
            propertyTitle,
            propertyImage
          };
        })
      );
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to fetch bookings. Please try again.');
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    if (searchTerm) {
      filtered = filtered.filter(booking =>
        (booking.guestName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.propertyTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.id || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(booking => booking.status === filterStatus);
    }

    setFilteredBookings(filtered);
    setCurrentPage(1);
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status,
        updatedAt: new Date()
      });

      setBookings(bookings.map(booking =>
        booking.id === bookingId
          ? { ...booking, status }
          : booking
      ));

      toast.success(`Booking ${status} successfully`);
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, text: 'Pending Review', icon: Clock },
      confirmed: { bg: colors.successGhost, color: colors.success, text: 'Confirmed', icon: CheckCircle },
      ongoing: { bg: colors.primaryGhost, color: colors.primary, text: 'Active Guest', icon: Calendar },
      completed: { bg: colors.surfaceHover, color: colors.textSecondary, text: 'Completed', icon: CheckCircle },
      cancelled: { bg: colors.errorGhost, color: colors.error, text: 'Cancelled', icon: XCircle },
      rejected: { bg: colors.errorGhost, color: colors.error, text: 'Rejected', icon: XCircle }
    };

    const strStatus = normalizeBookingStatus(status);
    const badge = badges[strStatus] || {
      bg: colors.backgroundSecondary,
      color: colors.textMuted,
      text: status ? String(status).replace(/_/g, ' ') : 'Unknown',
      icon: Clock
    };
    const Icon = badge.icon;

    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    ongoing: bookings.filter(b => b.status === 'ongoing').length,
    revenue: bookings.reduce((sum, b) => sum + getBookingAmount(b), 0)
  };

  const kpis = [
    { id: 'all', label: 'Total Reservations', value: stats.total, icon: Calendar, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'pending', label: 'Awaiting Confirmation', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'confirmed', label: 'Upcoming Stays', value: stats.confirmed, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'ongoing', label: 'Currently Hosting', value: stats.ongoing, icon: BarChart3, color: colors.secondary, gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' },
    { id: 'revenue', label: 'Gross Volume (SAR)', value: stats.revenue.toLocaleString(), icon: DollarSign, color: colors.success, gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)', isValueLarge: true },
  ];

  if (error) {
    return (
       <div className="flex flex-col items-center justify-center p-16 card-premium animate-fadeIn mx-6 mt-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.errorGhost }}>
             <XCircle className="w-10 h-10" style={{ color: colors.error }} />
          </div>
          <h3 className="text-[16px] font-bold" style={{ color: colors.text }}>Failed to load transactions</h3>
          <p className="mt-1.5 text-[14px]" style={{ color: colors.textSecondary }}>{error}</p>
          <button onClick={fetchBookings} className="mt-6 px-6 py-2.5 rounded-xl font-bold bg-primary text-white shadow-md hover:-translate-y-0.5 transition-all">Try Again</button>
       </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Reservation Center
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Track and manage all stay booking workflows globally.
          </p>
        </div>
        <button 
          onClick={fetchBookings}
          className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors flex items-center gap-2"
          style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Sync Engine
        </button>
      </div>

       {/* KPI Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filterStatus === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => kpi.id !== 'revenue' ? setFilterStatus(kpi.id) : null}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md bg-white border border-white/20"
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-4 h-4 text-white drop-shadow" />
                  </div>
                  <p className={`font-bold ${kpi.isValueLarge ? 'text-lg text-green-500' : 'text-2xl'}`} style={{ color: kpi.isValueLarge ? undefined : colors.text }}>{kpi.value}</p>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filter Bar */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search by Booking ID, Guest, or Property..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
              style={{ 
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
                color: colors.text
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.borderActive; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          
          <div className="relative min-w-[200px]">
             <Filter className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
             <select
               className="w-full pl-11 pr-8 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none appearance-none cursor-pointer"
               style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
               value={filterStatus}
               onChange={(e) => setFilterStatus(e.target.value)}
             >
               <option value="all">All Statuses</option>
               <option value="pending">Pending</option>
               <option value="confirmed">Confirmed</option>
               <option value="ongoing">Ongoing</option>
               <option value="completed">Completed</option>
               <option value="cancelled">Cancelled</option>
             </select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="card-premium overflow-hidden" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
           <div className="flex flex-col items-center justify-center p-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
              <p className="mt-4 text-[14px] font-medium" style={{ color: colors.textSecondary }}>Loading ledger...</p>
           </div>
        ) : filteredBookings.length === 0 ? (
           <div className="flex flex-col items-center justify-center p-16">
             <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
                <Calendar className="w-8 h-8" style={{ color: colors.textMuted }} />
             </div>
             <h3 className="text-[15px] font-bold" style={{ color: colors.text }}>No Reservations Found</h3>
             <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
                {bookings.length === 0 ? 'Your reservation ledger is currently empty.' : 'Try adjusting the search criteria.'}
             </p>
           </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Itinerary</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Stay Duration</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Ledger Amount</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Verification Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((booking, index) => (
                  <tr 
                    key={booking.id}
                    className="group transition-colors hover:bg-opacity-80" 
                    style={{ borderBottom: index < filteredBookings.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4 min-w-[300px]">
                      <div className="flex items-center gap-4">
                        {/* Property Image Thumbnail */}
                        <div className="h-14 w-14 rounded-xl flex-shrink-0 bg-cover bg-center border shadow-sm" style={{ borderColor: colors.border, backgroundImage: booking.propertyImage ? `url(${booking.propertyImage})` : 'none', backgroundColor: colors.backgroundSecondary }}>
                           {!booking.propertyImage && <Building className="w-5 h-5 mx-auto mt-4" style={{ color: colors.textMuted }}/>}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                             <div className="w-4 h-4 rounded-full bg-cover bg-center ring-1 ring-white/20" style={{ backgroundImage: booking.guestAvatar ? `url(${booking.guestAvatar})` : 'none', background: !booking.guestAvatar ? 'linear-gradient(135deg, #10B981, #059669)' : undefined }}></div>
                             <span className="text-[12px] font-bold uppercase tracking-wide" style={{ color: colors.textSecondary }}>{booking.guestName}</span>
                          </div>
                          <div className="text-[15px] font-extrabold truncate" style={{ color: colors.text }}>
                            {booking.propertyTitle}
                          </div>
                          <div className="text-[11px] font-bold tracking-wide uppercase mt-1 px-1.5 py-0.5 rounded inline-block" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textMuted }}>
                            ID: {booking.id.slice(0,8)}... • {booking.guests || 1} Pax
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center gap-3">
                          <div className="text-right">
                             <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>In</p>
                             <p className="text-[13px] font-bold" style={{ color: colors.text }}>{formatDate(booking.checkIn)}</p>
                          </div>
                          <div className="w-8 border-b-2" style={{ borderColor: `${colors.primary}40` }}></div>
                          <div>
                             <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>Out</p>
                             <p className="text-[13px] font-bold" style={{ color: colors.text }}>{formatDate(booking.checkOut)}</p>
                          </div>
                       </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[15px] font-black" style={{ color: colors.primary }}>
                        {formatCurrency(booking.amount)}
                      </div>
                      {booking.paymentMethod && <div className="text-[11px] font-bold uppercase tracking-wider mt-0.5" style={{ color: colors.textMuted }}>Via {booking.paymentMethod}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                       <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="px-3 py-2 rounded-xl text-[12px] font-bold transition-all inline-flex items-center gap-1.5 hover:scale-105"
                            style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}
                          >
                            <Eye className="w-[14px] h-[14px]" /> Review
                          </button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && filteredBookings.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredBookings.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Booking Detail Glassmorphism Modal */}
      {selectedBooking && (
        <div className="admin-modal-overlay fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div className="admin-modal-card admin-modal-scroll rounded-3xl max-w-xl w-full p-5 sm:p-8 shadow-2xl relative flex flex-col animate-fadeInScale" style={{ backgroundColor: colors.surface }}>
            
            {/* Soft decorative blob */}
            <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-[80px]" style={{ backgroundColor: `${colors.primary}30` }}></div>

            <div className="relative z-10">
               <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
                 <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 rounded-2xl flex flex-col justify-center items-center shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white border border-white/20">
                       <Calendar className="w-6 h-6" />
                    </div>
                    <div>
                       <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: colors.text }}>Folio Overview</h3>
                       <p className="text-[13px] font-bold font-mono tracking-wider opacity-60 mt-0.5" style={{ color: colors.textSecondary }}>REF: {selectedBooking.id}</p>
                    </div>
                 </div>
                 <button onClick={() => setSelectedBooking(null)} className="p-2.5 rounded-full hover:rotate-90 transition-all duration-300 bg-white/5 backdrop-blur shadow-sm">
                   <X className="w-5 h-5" style={{ color: colors.text }} />
                 </button>
               </div>

               <div className="p-1 rounded-2xl border" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5">
                     
                     <div className="p-4 bg-inherit rounded-tl-xl">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Principal Guest</p>
                        <div className="flex items-center gap-2">
                           <div className="w-6 h-6 rounded-full bg-cover ring-2 ring-white/10" style={{ backgroundImage: selectedBooking.guestAvatar ? `url(${selectedBooking.guestAvatar})` : 'none', backgroundColor: colors.primary }}></div>
                           <p className="text-[14px] font-bold truncate" style={{ color: colors.text }}>{selectedBooking.guestName}</p>
                        </div>
                     </div>

                     <div className="p-4 bg-inherit rounded-tr-xl">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Booked Estate</p>
                        <p className="text-[14px] font-bold truncate" style={{ color: colors.text }}>{selectedBooking.propertyTitle}</p>
                     </div>

                     <div className="p-4 bg-inherit">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Arrival</p>
                        <p className="text-[15px] font-bold" style={{ color: colors.text }}>{formatDate(selectedBooking.checkIn)}</p>
                     </div>
                     
                     <div className="p-4 bg-inherit">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Departure</p>
                        <p className="text-[15px] font-bold" style={{ color: colors.text }}>{formatDate(selectedBooking.checkOut)}</p>
                     </div>

                     <div className="p-4 bg-inherit rounded-bl-xl">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Total Consideration</p>
                        <div className="flex items-baseline gap-1">
                           <p className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600">{formatCurrency(selectedBooking.amount)}</p>
                        </div>
                     </div>

                     <div className="p-4 bg-inherit rounded-br-xl">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: colors.textMuted }}>Execution Status</p>
                        <div>{getStatusBadge(selectedBooking.status)}</div>
                     </div>

                  </div>
               </div>

               {selectedBooking.status === 'pending' && (
                 <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-dashed" style={{ borderColor: colors.border }}>
                   <button
                     onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                     className="flex-[2] py-4 rounded-xl font-bold text-white transition-all shadow-[0_4px_14px_rgba(34,197,94,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(34,197,94,0.4)] flex items-center justify-center gap-2"
                     style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                   >
                     <CheckCircle className="w-5 h-5" /> Force Confirm
                   </button>
                   <button
                     onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                     className="flex-[1] py-4 rounded-xl font-bold text-white transition-all shadow-[0_4px_14px_rgba(239,68,68,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(239,68,68,0.4)] flex items-center justify-center gap-2"
                     style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                   >
                     <XCircle className="w-5 h-5" /> Cancel
                   </button>
                 </div>
               )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
